// client/src/components/business/BusinessCard.js
import React, { useState, useEffect, Suspense, memo, useMemo, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import LazyImage from "../common/LazyImage";
import StarRating from "../common/StarRating";
import LoadingSpinner from "../common/LoadingSpinner";
import ActionButton from "./ActionButtons";
import { useUserAuth } from "../../contexts/UserAuthContext";
import toast from "react-hot-toast";
import { getImageUrl } from "../../utils/imageHelper";
import { getCityDisplayName } from "../../utils/cityHelper";
import { trackEvent } from "../../utils/analytics";
// CSS loaded at page level - removed duplicate import

// Lazy load the ReportIssueModal
const ReportIssueModal = React.lazy(() => import("../modals/ReportIssueModal"));

// Simplified comparison function for React.memo
const areBusinessPropsEqual = (prevProps, nextProps) => {
    const prev = prevProps.business;
    const next = nextProps.business;

    // Quick reference check - most common case
    if (prev === next) return true;

    // Only compare essential properties that affect rendering
    return (
        prev._id === next._id &&
        prev.businessName === next.businessName &&
        prev.verified === next.verified &&
        prev.profileImage === next.profileImage
    );
};

const BusinessCard = memo(({ business }) => {
    const { t, i18n } = useTranslation();
    const {
        _id,
        businessName,
        categories,  // Changed from category to categories array
        businessType,
        cities,  // Changed from city to cities array
        verified,
        mobile,
        profileImage,
        socialLinks,
        facebookLikes,
        averageRating,  // Get rating from database
    } = business;

    // Extract first category name and count for display (SIMPLIFIED multi-category approach)
    const firstCategory = categories?.[0];
    const firstCategoryName = firstCategory?.name
        ? firstCategory.name[i18n.language] || firstCategory.name.en
        : '';
    const additionalCategoryCount = categories && categories.length > 1 ? categories.length - 1 : 0;

    // Extract first city and count for display (same approach as categories)
    const firstCity = cities && cities.length > 0 ? getCityDisplayName(cities[0], i18n.language) : '';
    const additionalCityCount = cities && cities.length > 1 ? cities.length - 1 : 0;

    const { user, isAuthenticated, updateUserFavorites } = useUserAuth();

    // State for report issue modal
    const [isReportModalOpen, setIsReportModalOpen] = useState(false);

    // State for favorites functionality - optimized with local state for immediate UI feedback
    const [localFavoriteState, setLocalFavoriteState] = useState(null);
    const [isLoading, setIsLoading] = useState(false);

    // Memoized calculation of favorite status - only recalculates when user.favorites or _id changes
    const isFavoriteFromUser = useMemo(() => {
        return user && user.favorites ? user.favorites.includes(_id) : false;
    }, [user, _id]);

    // Display favorite state: local state takes precedence during loading, otherwise use user state
    const isFavorited = localFavoriteState !== null ? localFavoriteState : isFavoriteFromUser;

    // Reset local state when user favorites change
    useEffect(() => {
        setLocalFavoriteState(null);
    }, [isFavoriteFromUser]);

    // Memoized event handlers - prevent unnecessary re-renders of child components
    const handleImageError = useCallback((e) => {
        e.target.src = "/placeholder-business.png";
    }, []);

    // Handler functions for report issue modal
    const handleReportIssue = useCallback((e) => {
        e.preventDefault(); // Prevent navigation when clicking report
        e.stopPropagation(); // Prevent event bubbling
        setIsReportModalOpen(true);
        // Analytics: report modal opened
        trackEvent("report_issue_open", { business_id: _id, business_name: businessName });
    }, [_id, businessName]);

    // Close modal handler
    const handleCloseReportModal = useCallback(() => {
        setIsReportModalOpen(false);
    }, []);


    // Card ref for focus management
    const cardRef = useRef(null);

    // Keyboard navigation handler
    const handleKeyDown = useCallback((e) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            // Navigate to business detail page
            window.location.href = `/business/${_id}`;
        }
    }, [_id]);


    // Favorite button handler with real API calls - memoized for performance
    const handleFavoriteClick = useCallback(async (e) => {
        e.preventDefault(); // Prevent navigation
        e.stopPropagation(); // Prevent event bubbling

        // Check if user is authenticated
        if (!isAuthenticated) {
            toast.error(t('errors.loginToSaveFavorites'));
            return;
        }

        // Prevent multiple clicks
        if (isLoading) return;

        setIsLoading(true);
        const previousState = isFavorited;

        // Optimistic update using local state for immediate UI feedback
        setLocalFavoriteState(!isFavorited);
        // Analytics: favorite toggle attempt (pre-API)
        trackEvent("favorite_toggle", {
            business_id: _id,
            business_name: businessName,
            to_state: !previousState ? "on" : "off",
            location: "card",
        });

        try {
            // Use centralized axios instance to ensure base URL and headers
            const { userApi } = await import("../../services/api");
            const response = await userApi.post(`/auth/favorites/toggle/${_id}`);
            const data = response.data;

            // PERFORMANCE: Update favorites array and count for visual state
            updateUserFavorites(_id, data.action, data.favoritesCount);

            // Show success message
            toast.success(data.message);

            // Analytics: favorite toggle success
            trackEvent("favorite_toggle_success", {
                business_id: _id,
                action: data.action,
                favorites_count: data.favoritesCount,
            });

        } catch (error) {
            console.error("Favorites toggle error:", error);
            
            // Rollback optimistic update using local state
            setLocalFavoriteState(previousState);

            // Show error message
            const message = error?.response?.data?.message || error?.message || t('errors.failedToUpdateFavorites');
            toast.error(message);

            // Analytics: favorite toggle error
            trackEvent("favorite_toggle_error", {
                business_id: _id,
                message: error?.message || "unknown_error",
            });
        } finally {
            setIsLoading(false);
        }
    }, [isAuthenticated, isLoading, isFavorited, _id, updateUserFavorites, t, businessName]); // Dependencies for useCallback

    return (
        <>
            <div
                className="business-card"
                ref={cardRef}
                role="article"
                tabIndex="0"
                onKeyDown={handleKeyDown}
                aria-label={`${businessName} business listing`}
            >
                {/* UPDATED: New Facebook-style circular image container */}
                <div className="business-image-circular-container">
                    <div className="business-image-circular">
                        {profileImage ? (
                            <LazyImage
                                width={75}
                                height={75}
                                decoding="async"
                                src={getImageUrl(profileImage, 'thumbnail')}
                                alt={businessName}
                                onError={handleImageError}
                                placeholder={
                                    <div className="image-placeholder-circular">
                                        <span>{businessName.charAt(0)}</span>
                                    </div>
                                }
                            />
                        ) : (
                            <div className="image-placeholder-circular">
                                <span>{businessName.charAt(0)}</span>
                            </div>
                        )}
                    </div>

                    {/* UPDATED: Verification badge positioned outside circular image */}
                    {verified && (
                        <span className="verified-badge-circular" role="img" aria-label={t('business.verified') || 'Verified business'}><svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M9 16.17 4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" /></svg></span>
                    )}

                    {/* NEW: Facebook likes counter - now links to Facebook page when available */}
                    {socialLinks?.facebook ? (
                        <a
                            href={socialLinks.facebook}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="facebook-likes-counter"
                            aria-label={t('actions.viewFacebookPage') || 'Open Facebook page in new tab'}
                            title={t('actions.viewFacebookPage') || 'Open Facebook page'}
                            onClick={() => trackEvent("social_click", { platform: "facebook", surface: "likes_counter", business_id: _id, business_name: businessName })}
                        >
                            <svg className="likes-icon" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                                <path d="M2 21h4V9H2v12zm19-11c0-1.1-.9-2-2-2h-6l1-5-7 7v10h11c1.1 0 2-.9 2-2v-8z" />
                            </svg>
                            <span className="likes-count">{(facebookLikes || 0).toLocaleString()}</span>
                        </a>
                    ) : (
                        <div className="facebook-likes-counter" title="Facebook Page Likes" aria-label="Facebook Page Likes">
                            <svg className="likes-icon" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                                <path d="M2 21h4V9H2v12zm19-11c0-1.1-.9-2-2-2h-6l1-5-7 7v10h11c1.1 0 2-.9 2-2v-8z" />
                            </svg>
                            <span className="likes-count">{(facebookLikes || 0).toLocaleString()}</span>
                        </div>
                    )}

                    {/* UPDATED: Desktop overlay icons positioned near title area */}
                    <div className="desktop-overlay-icons">
                        <ActionButton
                            type="favorite"
                            isActive={isFavorited}
                            isLoading={isLoading}
                            onClick={handleFavoriteClick}
                            businessName={businessName}
                            size="large"
                        />

                        <ActionButton
                            type="report"
                            onClick={handleReportIssue}
                            businessName={businessName}
                            size="large"
                        />
                    </div>
                </div>

                <div className="business-info">
                    {/* Business name with mobile icons (hidden on desktop) */}
                    <div className="business-name-header">
                        <h3 className="business-name">
                            {/* Navigate to business detail page */}
                            <Link to={`/business/${_id}`}>
                                {businessName}
                            </Link>
                        </h3>

                        {/* Mobile-only icon group (hidden on desktop 1024px+) */}
                        <div className="icon-group-mobile">
                            <ActionButton
                                type="favorite"
                                isActive={isFavorited}
                                isLoading={isLoading}
                                onClick={handleFavoriteClick}
                                businessName={businessName}
                                size="small"
                            />

                            <ActionButton
                                type="report"
                                onClick={handleReportIssue}
                                businessName={businessName}
                                size="small"
                            />
                        </div>
                    </div>

                    {/* Star rating component - display real rating from database */}
                    <StarRating
                        rating={averageRating}
                        size="medium"
                        showNumber={true}
                        className="business-rating"
                    />

                    {/* Business meta - updated for multi-category and multi-city support */}
                    <div className="business-meta">
                        <span className="category">
                            {firstCategoryName}
                            {additionalCategoryCount > 0 && ` +${additionalCategoryCount}`}
                        </span>
                        <span className="type">{t(`business.${businessType}`)}</span>
                        <span className="city">
                            {firstCity}
                            {additionalCityCount > 0 && ` +${additionalCityCount}`}
                        </span>
                    </div>

                    {/* Contact section with clickable phone number */}
                    <div className="business-contact">
                        <a
                            href={`tel:${mobile}`}
                            className="mobile"
                            aria-label={`Call ${businessName} at ${mobile}`}
                            onClick={() => trackEvent("call_click", { business_id: _id, business_name: businessName, phone: mobile })}
                        >
                            <svg className="phone-icon" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                                <path d="M6.62 10.79a15.05 15.05 0 006.59 6.59l2.2-2.2a1 1 0 011.01-.24c1.1.37 2.29.57 3.58.57a1 1 0 011 1V21a1 1 0 01-1 1C11.4 22 2 12.6 2 1a1 1 0 011-1h3.5a1 1 0 011 1c0 1.29.2 2.48.57 3.58a1 1 0 01-.24 1.01l-2.21 2.2z" />
                            </svg>
                            {mobile}
                        </a>
                    </div>

                    {/* Social Links - always rendered to maintain consistent card heights */}
                    <div className="social-links">
                        {socialLinks?.facebook && (
                                <a
                                    href={socialLinks.facebook}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="social-icon facebook"
                                    title="Visit our Facebook page"
                                    aria-label="Facebook"
                                    onClick={() => trackEvent("social_click", { platform: "facebook", business_id: _id, business_name: businessName })}
                                >
                                    <svg
                                        viewBox="0 0 24 24"
                                        fill="currentColor"
                                    >
                                        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                                    </svg>
                                </a>
                            )}
                        {socialLinks?.instagram && (
                                <a
                                    href={socialLinks.instagram}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="social-icon instagram"
                                    title="Visit our Instagram page"
                                    aria-label="Instagram"
                                    onClick={() => trackEvent("social_click", { platform: "instagram", business_id: _id, business_name: businessName })}
                                >
                                    <svg
                                        viewBox="0 0 24 24"
                                        fill="currentColor"
                                    >
                                        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                                    </svg>
                                </a>
                            )}
                        {socialLinks?.tiktok && (
                                <a
                                    href={socialLinks.tiktok}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="social-icon tiktok"
                                    title="Visit our TikTok page"
                                    aria-label="TikTok"
                                    onClick={() => trackEvent("social_click", { platform: "tiktok", business_id: _id, business_name: businessName })}
                                >
                                    <svg
                                        viewBox="0 0 24 24"
                                        fill="currentColor"
                                    >
                                        <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 4.08-.01 8.16-.02 12.24-.03 1.83-.74 3.66-1.97 5.08-1.23 1.42-2.91 2.44-4.73 2.8-1.82.36-3.74.01-5.35-.98-1.61-.99-2.91-2.5-3.59-4.26-.68-1.76-.63-3.73.13-5.45.76-1.72 2.09-3.16 3.68-3.96 1.59-.8 3.45-.85 5.08-.13v4.56c-.6-.24-1.28-.35-1.94-.23-.66.12-1.28.5-1.73 1.05-.45.55-.7 1.26-.7 1.97 0 .71.25 1.42.7 1.97.45.55 1.07.93 1.73 1.05.66.12 1.34.01 1.94-.23.6-.24 1.12-.65 1.48-1.18.36-.53.56-1.16.56-1.79V.02h4.55z" />
                                    </svg>
                                </a>
                            )}
                        {socialLinks?.youtube && (
                                <a
                                    href={socialLinks.youtube}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="social-icon youtube"
                                    title="Visit our YouTube channel"
                                    aria-label="YouTube"
                                    onClick={() => trackEvent("social_click", { platform: "youtube", business_id: _id, business_name: businessName })}
                                >
                                    <svg
                                        viewBox="0 0 24 24"
                                        fill="currentColor"
                                    >
                                        <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
                                    </svg>
                                </a>
                            )}
                    </div>
                </div>
            </div>

            {/* Report Issue Modal - Portaled to document.body to avoid overflow clipping */}
            {isReportModalOpen && createPortal(
                <Suspense fallback={<div style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', zIndex: 9999 }}><LoadingSpinner /></div>}>
                    <ReportIssueModal
                        isOpen={isReportModalOpen}
                        onClose={handleCloseReportModal}
                        businessId={_id}
                        businessName={businessName}
                    />
                </Suspense>,
                document.body
            )}
        </>
    );
}, areBusinessPropsEqual);

// Add display name for better debugging and React DevTools
BusinessCard.displayName = 'BusinessCard';

export default BusinessCard;

